import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import styles from "./DataGrid.module.css";
import btnStyles from "../InputControls/Button.module.css";
import * as React from "react";
import PropTypes from "prop-types";
import { createGrid } from "ag-grid-community";
import _ from "lodash";
import { readExcelRows, saveGridLikeWorkbook } from "@maxis/client-logic/utils/componentUtil";
import { validate, getValidateObject, getObjectFromYml } from "@maxis/client-logic/utils/validationUtil";
import { getDate2String } from "@maxis/client-logic/utils/dataUtil";
import { normalizeArrayData } from "@maxis/client-logic/utils/dataShapeUtil";
import { PAGE_GAP, pageWindow } from "@maxis/client-logic/utils/serverPagingUtil";
import { createBtnArea } from "../PageLayout/FormSection";
import { DataGridContextMenu } from "./DataGridContextMenu";

const ROW_STATE_ADDED = 1;
const ROW_STATE_EDITED = 2;
const ROW_STATE_DELETED = 4;
const ROW_STATE_NORMAL = 8;

const CELL_PANEL = "Cell";
const HEADER_PANEL = "ColumnHeader";

class InlineSelectCellEditor {
  init(params) {
    this.params = params;
    this.eInput = document.createElement("select");
    this.eInput.className = styles.inlineSelectEditor;
    this.eInput.setAttribute("aria-label", params.colDef?.headerName || params.colDef?.field || "select");

    const values = Array.isArray(params.values) ? params.values : [];
    const labels = params.lookupLabels || {};
    values.forEach((value) => {
      const option = document.createElement("option");
      option.value = value == null ? "" : String(value);
      option.textContent = labels[value] ?? value ?? "";
      this.eInput.appendChild(option);
    });

    this.eInput.value = params.value == null ? "" : String(params.value);
    this.eInput.addEventListener("change", () => params.stopEditing?.());
  }

  getGui() {
    return this.eInput;
  }

  afterGuiAttached() {
    this.eInput?.focus();
  }

  getValue() {
    return this.eInput?.value ?? "";
  }

  isPopup() {
    return false;
  }
}

class HeaderCheckboxComponent {
  init(params) {
    this.owner = params.owner;
    this.eGui = this.owner?.createHeaderCheckboxElement?.() || document.createElement("span");
  }

  getGui() {
    return this.eGui;
  }

  refresh(params) {
    // Update the input we already handed over. Building a new element here looked
    // equivalent but was dead code: ag-Grid reads getGui() once, at creation, so the
    // replacement never reached the DOM and the header checkbox never re-synced with
    // the rows -- which is what made "clear all" take two clicks.
    this.owner = params.owner;
    this.owner?.syncHeaderCheckbox?.();
    return true;
  }
}

class DataGrid extends React.Component {
  tableHostRef = React.createRef();
  useRefInput = React.createRef();
  refGrid = React.createRef();
  refProgramParam = React.createRef(null);
  refSelectedIndex = React.createRef(-1);
  refSearchCnt = React.createRef(0);

  static createSortItem(field, status = true) {
    return { property: field, ascending: status };
  }

  constructor(props) {
    super(props);

    const initialData = this.normalizeInputData(props.source || props.data || []);
    this.state = {
      columns: props.columns || [],
      columnGroups: props.columnGroups || [],
      data: initialData,
      rowCount: initialData.length,
      isReadOnly: props.isReadOnly,
      bufferedStartIndex: props.bufferedStartIndex,
      bufferedDataCount: props.bufferedDataCount,
      bufferedEndIndex: null,
      pageButton: [],
      activeButton: [],
      validateInfo: null,
    };

    this.table = null;
    this.gridApi = null;
    this.selectedIndex = -1;
    this.selectedColIndex = 0;
    this.clickTimer = null;
    this.dropDowns = {};
    this.excelHeaderRowIdx = 0;
    this.excelCallback = null;
    this.contextMenu = null;
    this.checkBoxColumn = props.checkBoxColumn;
    this.gridAdapter = this.createGridAdapter();
    this.refGrid.current = this.gridAdapter;
    this.collectionLike = this.createCollectionLike();
    this.pendingEditEvents = new Map();
    this.pendingSelection = null;
    this.resizeObserver = null;
    this.resizeFallback = null;
  }

  componentDidMount() {
    this.initializeTable();
    this.observeGridSize();
    this.loadValidationInfo(this.props.validateYmlFile);

    const source = this.props.source || this.props.data;
    if (source) {
      this.setDataBinding(source);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.columns !== this.props.columns && this.props.columns) {
      this.setColumns(this.props.columns);
    }

    if (prevProps.columnGroups !== this.props.columnGroups && this.props.columnGroups) {
      this.setColumngroups(this.props.columnGroups);
    }

    if (prevProps.data !== this.props.data && this.props.data) {
      this.setDataBinding(this.props.data);
    }

    if (prevProps.source !== this.props.source && this.props.source) {
      this.setDataBinding(this.props.source);
    }

    if (prevProps.isReadOnly !== this.props.isReadOnly) {
      this.setIsReadOnly(this.props.isReadOnly);
    }

    if (prevProps.validateYmlFile !== this.props.validateYmlFile) {
      this.loadValidationInfo(this.props.validateYmlFile);
    }

    if (
      prevState.columns !== this.state.columns ||
      prevState.columnGroups !== this.state.columnGroups ||
      prevState.isReadOnly !== this.state.isReadOnly
    ) {
      this.applyColumns();
    }
  }

  componentWillUnmount() {
    if (this.clickTimer) clearTimeout(this.clickTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeFallback) window.removeEventListener("resize", this.resizeFallback);
    if (this.tableHostRef.current) {
      this.tableHostRef.current.removeEventListener("click", this.handleNativeHeaderClick);
      this.tableHostRef.current.removeEventListener("dblclick", this.handleNativeHeaderDoubleClick);
    }
    // The context menu owns a <body> node and a document-level listener, so it has to be
    // torn down here or it survives every navigation.
    this.contextMenu?.destroy?.();
    this.contextMenu = null;
    if (this.table) {
      this.table.destroy?.();
      this.table = null;
      this.gridApi = null;
    }
  }

  loadValidationInfo = (validateYmlFile) => {
    if (!validateYmlFile) return;
    getObjectFromYml(validateYmlFile).then((res) => {
      this.setState({ validateInfo: res });
    });
  };

  initializeTable = () => {
    if (!this.tableHostRef.current || this.table) return;

    this.table = createGrid(this.tableHostRef.current, {
      rowData: this.state.data,
      columnDefs: this.getAgGridColumns(),
      getRowId: (params) => String(params.data?.__e3_grid_id ?? ""),
      rowSelection: {
        mode: "singleRow",
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: false,
      },
      enableBrowserTooltips: true,
      suppressMovableColumns: !this.props.isAllowDragging,
      headerHeight: this.props.headersVisibility === "None" ? 0 : 36,
      rowHeight: this.props.rowHeight,
      animateRows: false,
      defaultColDef: {
        resizable: true,
        sortable: this.props.isAllowSorting !== false,
        editable: false,
        minWidth: 60,
        cellDataType: false,
      },
      onCellClicked: (event) => this.handleCellClick(event, false),
      onCellDoubleClicked: (event) => this.handleCellClick(event, true),
      onCellEditingStarted: (event) => this.handleCellEditing(event),
      onCellValueChanged: (event) => this.handleCellEdited(event),
      onSortChanged: () => this.callProp("sortedColumn", this.gridAdapter, this.makeEvent()),
      onGridReady: (event) => {
        this.gridApi = event.api;
        this.redrawTable();
        this.callProp("onloadedRows", this.gridAdapter, this.makeEvent());
      },
      onFirstDataRendered: () => {
        this.redrawTable();
        this.flushPendingSelection();
        this.callProp("onloadedRows", this.gridAdapter, this.makeEvent());
      },
      onRowDataUpdated: () => {
        this.redrawTable();
        this.flushPendingSelection();
        this.callProp("onloadedRows", this.gridAdapter, this.makeEvent());
      },
    });
    this.gridApi = this.table;

    this.bindTableEvents();
    this.refGrid.current = this.gridAdapter;

    if (this.props.isContextMenu) {
      this.contextMenu = new DataGridContextMenu(
        this.gridAdapter,
        this.props.exportFileName || "grid"
      );
    }
  };

  bindTableEvents = () => {
    if (!this.table) return;

    if (!this.tableHostRef.current) return;
    this.tableHostRef.current.addEventListener("click", this.handleNativeHeaderClick);
    this.tableHostRef.current.addEventListener("dblclick", this.handleNativeHeaderDoubleClick);
  };

  observeGridSize = () => {
    const target = this.tableHostRef.current?.parentElement || this.tableHostRef.current;
    if (!target || !this.table) return;

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.redrawTable());
      this.resizeObserver.observe(target);
      return;
    }

    this.resizeFallback = () => this.redrawTable();
    window.addEventListener("resize", this.resizeFallback);
  };

  redrawTable = () => {
    if (!this.table) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const host = this.tableHostRef.current;
        const bodyViewport = host?.querySelector?.(".ag-body-viewport");
        if (!this.gridApi || !host || host.offsetWidth <= 0 || bodyViewport?.offsetWidth <= 0) return;
        this.gridApi.refreshCells?.({ force: true });
      });
    });
  };

  getFlatColumns = (columns = this.state.columnGroups?.length ? this.state.columnGroups : this.state.columns) => {
    const result = [];

    (columns || []).forEach((column) => {
      if (column?.columns?.length) {
        result.push(...this.getFlatColumns(column.columns));
      } else if (column) {
        result.push(column);
      }
    });

    return result;
  };

  getAgGridColumns = () => {
    const source = this.state.columnGroups?.length ? this.state.columnGroups : this.state.columns;
    return (source || []).map((column) => this.toAgGridColumn(column)).filter(Boolean);
  };

  toAgGridColumn = (column) => {
    if (!column) return null;
    if (column.columns?.length) {
      return {
        headerName: column.header || column.title || "",
        children: column.columns.map((child) => this.toAgGridColumn(child)).filter(Boolean),
      };
    }

    const binding = column.binding;
    if (!binding) return null;

    const isCheckColumn = binding === this.getCheckBoxColumn();
    const editable = column.isReadOnly !== true && (this.state.isReadOnly !== true || column.isReadOnly === false);
    const lookupItems = this.getLookupItems(column);
    const lookupValues = this.getLookupValues(binding, lookupItems, column.dataMapCode, column.dataMapValue);
    const alignment = this.resolveColumnAlignment(column);
    const headerAlignment = this.resolveHeaderAlignment(column, isCheckColumn);
    const width = this.resolveColumnWidth(column.width);
    const flex = Number(column.flex);
    const hasFlex = Number.isFinite(flex) && flex > 0;

    const agColumn = {
      headerName: column.header ?? binding,
      field: binding,
      width: hasFlex ? undefined : width,
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
      flex: hasFlex ? flex : width ? undefined : 1,
      cellStyle: (params) => ({
        textAlign: alignment,
        justifyContent: this.resolveJustifyContent(alignment),
        ...(alignment === "right" && { fontVariantNumeric: "tabular-nums" }),
        ...(params.data?.ROW_STATUS === ROW_STATE_DELETED && {
          backgroundColor: "#fff7f7",
        }),
        ...(params.data?.ROW_STATUS === ROW_STATE_DELETED && column.binding === "ROW_STATUS" && {
          color: "#9f1c1c",
          fontWeight: 600,
          justifyContent: "center",
        }),
        ...(params.data?.ROW_STATUS === ROW_STATE_DELETED && column.strikeWhenDeleted && {
          textDecoration: "line-through",
          textDecorationThickness: "1px",
        }),
      }),
      headerClass: `ag-header-${headerAlignment}`,
      hide: column.visible === false,
      sortable: this.props.isAllowSorting !== false,
      // 정렬 전에도 정렬 가능한 컬럼임을 알 수 있게 한다 (hover 시에만 노출, DataGrid.module.css).
      unSortIcon: this.props.isAllowSorting !== false,
      cellEditor: this.resolveEditor(column, editable, isCheckColumn, lookupValues),
      cellEditorParams: (params) => this.resolveEditorParams(params, column, lookupItems, lookupValues, isCheckColumn),
      editable: (params) => this.resolveCellEditable(params, column, editable, isCheckColumn),
      valueFormatter: (params) => this.formatCellValue(params, column, lookupItems, isCheckColumn),
      tooltipValueGetter: (params) => {
        const displayValue = this.formatCellValue(params, column, lookupItems, isCheckColumn);
        return displayValue == null ? "" : String(displayValue);
      },
      cellRenderer: (params) => this.renderCell(params, column, lookupItems, isCheckColumn),
      ...(column.dataType === "Number" && {
        comparator: (valueA, valueB) => {
          const numberA = Number(String(valueA ?? "").replace(/,/g, ""));
          const numberB = Number(String(valueB ?? "").replace(/,/g, ""));
          if (Number.isNaN(numberA) && Number.isNaN(numberB)) return 0;
          if (Number.isNaN(numberA)) return -1;
          if (Number.isNaN(numberB)) return 1;
          return numberA - numberB;
        },
      }),
      onCellClicked: column.clickEvent
        ? (params) => column.clickEvent(params.event, this.makeCellContext(params, column))
        : undefined,
      cellClass: (params) => this.getCellClasses(params, column),
    };

    if (isCheckColumn && this.props.isAllCheckbox) {
      agColumn.headerName = "";
      agColumn.headerComponent = HeaderCheckboxComponent;
      agColumn.headerComponentParams = { owner: this };
    }

    return agColumn;
  };

  resolveColumnWidth = (width) => {
    if (!width || width === "*") return undefined;
    const numericWidth = Number(width);
    return Number.isFinite(numericWidth) ? numericWidth : undefined;
  };

  resolveAlign = (align) => {
    if (align === "left" || align === "center" || align === "right") return align;
    return undefined;
  };

  resolveColumnAlignment = (column = {}) => {
    const explicitAlign = this.resolveAlign(column.align);
    if (explicitAlign) return explicitAlign;

    if (column.dataType === "Number") return "right";

    if (
      column.dataType === "Boolean" ||
      column.dataType === "Date" ||
      column.dataType === "Time" ||
      column.isButton ||
      column.isImage ||
      column.binding === "NO" ||
      column.binding === "ROW_CHECK" ||
      column.binding === "ROW_STATUS"
    ) {
      return "center";
    }

    return "left";
  };

  resolveHeaderAlignment = (column = {}, isCheckColumn = false) => {
    const explicitAlign = this.resolveAlign(column.headerAlign);
    if (explicitAlign) return explicitAlign;

    if (column.dataType === "Number") return "right";

    if (
      isCheckColumn ||
      column.binding === "NO" ||
      column.binding === "ROW_CHECK" ||
      column.binding === "ROW_STATUS" ||
      column.isButton ||
      column.isImage
    ) {
      return "center";
    }

    return "left";
  };

  resolveJustifyContent = (align) => {
    if (align === "center") return "center";
    if (align === "right") return "flex-end";
    return "flex-start";
  };

  resolveHeight = () => {
    const { height } = this.props;
    if (typeof height === "number") return height < 100 ? 27 * height : height;
    if (!height || height === "100%") return undefined;
    const numericHeight = Number(height);
    return Number.isFinite(numericHeight) ? (numericHeight < 100 ? 27 * numericHeight : numericHeight) : height;
  };

  resolveEditor = (column, editable, isCheckColumn, lookupValues) => {
    if (!editable && !isCheckColumn) return undefined;
    if (lookupValues) return InlineSelectCellEditor;
    if (isCheckColumn || column.dataType === "Boolean") return undefined;
    if (column.dataType === "Number") return "agNumberCellEditor";
    if (column.dataType === "Date" || column.dataType === "Time") return "agTextCellEditor";
    return editable ? "agTextCellEditor" : undefined;
  };

  resolveEditorParams = (cell, column, lookupItems, lookupValues, isCheckColumn) => {
    const editEvent = this.getPendingEditEvent(cell, column);
    const overrideItems = editEvent?.selectOptions || editEvent?.itemsSource || editEvent?.dataMap;
    const overrideValues = this.getLookupValues(
      column.binding,
      overrideItems,
      column.dataMapCode || this.dropDowns[column.binding]?.codeKey,
      column.dataMapValue || this.dropDowns[column.binding]?.labelKey
    );

    if (overrideValues || lookupValues) {
      return {
        values: Object.keys(overrideValues || lookupValues || {}),
        lookupLabels: overrideValues || lookupValues || {},
      };
    }

    if (isCheckColumn || column.dataType === "Boolean") {
      return { tristate: false };
    }

    return {};
  };

  resolveCellEditable = (cell, column, editable, isCheckColumn) => {
    if (!editable && !isCheckColumn) return false;
    // 체크박스/Boolean 컬럼은 resolveEditor 가 의도적으로 에디터를 주지 않는다(클릭으로 토글).
    // 그런데 editable 을 true 로 남겨두면 ag-Grid 가 기본 에디터(agTextCellEditor)로
    // 폴백해서, 더블클릭 시 체크박스 셀이 텍스트 입력창으로 바뀐다.
    // 토글은 handleCellClick 의 isCheckboxCell 분기가 담당하므로 editable 과 무관하다.
    if (isCheckColumn || column.dataType === "Boolean") return false;
    const event = this.makeEditableCellEvent(cell, column);
    this.callProp("beginningEdit", this.gridAdapter, event);
    if (!event.cancel) this.callProp("prepareCellForEdit", this.gridAdapter, event);
    this.pendingEditEvents.set(this.getCellEditKey(cell, column), event);
    return !event.cancel;
  };

  getPendingEditEvent = (cell, column) => {
    const key = this.getCellEditKey(cell, column);
    return this.pendingEditEvents.get(key) || this.makeEditableCellEvent(cell, column);
  };

  getCellEditKey = (cell, column) => {
    const rowData = cell?.data;
    const rowKey = rowData?.__e3_grid_id || this.getRowIndex(rowData);
    return `${rowKey}:${column.binding}`;
  };

  makeEditableCellEvent = (cell, column) => {
    const event = this.makeCellEvent(cell);
    const dropdown = this.dropDowns[column.binding];
    let localItems = dropdown?.items || column.dataMap || [];

    Object.defineProperties(event, {
      itemsSource: {
        get: () => localItems,
        set: (items) => {
          localItems = Array.isArray(items) ? items : [];
        },
        configurable: true,
      },
      dataMap: {
        get: () => localItems,
        set: (items) => {
          localItems = Array.isArray(items) ? items : [];
        },
        configurable: true,
      },
      selectOptions: {
        get: () => localItems,
        set: (items) => {
          localItems = Array.isArray(items) ? items : [];
        },
        configurable: true,
      },
    });

    return event;
  };

  getLookupItems = (column) => {
    const dynamicItems = this.dropDowns[column.binding]?.items;
    if (dynamicItems) return dynamicItems;
    return column.dataMap || column.multiSelect?.dataMap || null;
  };

  getLookupValues = (binding, items, codeKey = "codeId", labelKey = "codeName") => {
    if (!items?.length) return null;
    const lookup = {};
    items.forEach((item) => {
      lookup[item?.[codeKey]] = item?.[labelKey] ?? item?.[codeKey];
    });

    if (!this.dropDowns[binding]) {
      this.dropDowns[binding] = { items, codeKey, labelKey };
    }

    return lookup;
  };

  formatCellValue = (params, column, lookupItems, isCheckColumn) => {
    const value = params.value;
    if (column.binding === "ROW_STATUS") {
      return this.renderRowStatus(params.data?.ROW_STATUS, column);
    }
    if (isCheckColumn || column.dataType === "Boolean") return value ? "true" : "false";
    if (lookupItems?.length) return this.getLookupLabel(column.binding, value);
    if (column.dataType === "Number") return this.formatNumberValue(value, column);
    if (column.dataType === "Date" || column.dataType === "Time") {
      return this.formatDateTimeValue(value, column);
    }
    return this.formatDisplayValue(value);
  };

  formatDateTimeValue = (value, column = {}) => {
    if (value === undefined || value === null || value === "") return "";

    const source = value instanceof Date ? value : String(value).trim();
    let date = value instanceof Date ? value : null;

    if (!date && /^\d{8}(\d{6})?$/.test(source)) {
      const year = Number(source.slice(0, 4));
      const month = Number(source.slice(4, 6)) - 1;
      const day = Number(source.slice(6, 8));
      const hour = Number(source.slice(8, 10) || 0);
      const minute = Number(source.slice(10, 12) || 0);
      const second = Number(source.slice(12, 14) || 0);
      date = new Date(year, month, day, hour, minute, second);
    }

    if (!date && column.dataType === "Time" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(source)) {
      const [hour, minute, second = 0] = source.split(":").map(Number);
      date = new Date(2000, 0, 1, hour, minute, second);
    }

    if (!date) date = new Date(source);
    if (Number.isNaN(date.getTime())) return value;

    const fallbackFormat = column.dataType === "Time" ? "HH:mm:ss" : "yyyy-MM-dd";
    return getDate2String(date, column.format || fallbackFormat);
  };

  renderCell = (params, column, lookupItems, isCheckColumn) => {
    const value = params.value;
    setTimeout(() => {
      if (params.eGridCell) this.callFormatItem(params, params.eGridCell);
    }, 0);

    if (column.binding === "ROW_STATUS") {
      return this.escapeHtml(this.renderRowStatus(params.data?.ROW_STATUS, column));
    }
    if (isCheckColumn || column.dataType === "Boolean") return this.renderCheckbox(value);
    if (column.dataType?.inputColor) {
      const color = this.escapeCssColor(value);
      return `<span class="colorbox" style="background:${color};"></span> ${this.escapeHtml(value || "")}`;
    }
    if (column.isButton) return this.renderButton(value, column);
    if (column.isImage) return this.renderImage(value, column);
    if (column.isLink) return this.renderLink(value);
    if (column.isFileTemplete) return `<span class="list_clip_icon"></span> ${this.escapeHtml(value)}`;
    if (lookupItems?.length) return this.escapeHtml(this.getLookupLabel(column.binding, value));
    if (column.dataType === "Number") {
      return this.escapeHtml(this.formatNumberValue(value, column));
    }
    if (column.dataType === "Date" || column.dataType === "Time") {
      return this.escapeHtml(this.formatDateTimeValue(value, column));
    }
    return this.escapeHtml(this.formatDisplayValue(value));
  };

  formatDisplayValue = (value) => {
    if (value == null) return "";
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item == null) return "";
          if (typeof item === "object") {
            return item.MODEL_ALIAS || item.MODEL_NAME || item.MODEL || item.NAME || item.name || JSON.stringify(item);
          }
          return String(item);
        })
        .filter(Boolean)
        .join(", ");
    }
    if (typeof value === "object") {
      return value.MODEL_ALIAS || value.MODEL_NAME || value.MODEL || value.NAME || value.name || JSON.stringify(value);
    }
    return value;
  };

  formatNumberValue = (value, column = {}) => {
    if (value === undefined || value === null || value === "") return "";

    const numericValue = typeof value === "number"
      ? value
      : Number(String(value).replace(/,/g, "").trim());
    if (!Number.isFinite(numericValue)) return value;

    return new Intl.NumberFormat("en-US", {
      useGrouping: true,
      minimumFractionDigits: column.minimumFractionDigits ?? 0,
      maximumFractionDigits: column.maximumFractionDigits ?? 8,
    }).format(numericValue);
  };

  getCellClasses = (params, column) => {
    const rowData = params.data || {};
    const classes = [];
    const alignment = this.resolveColumnAlignment(column);

    classes.push(`grid-align-${alignment}`);

    if (column.binding === "ROW_STATUS") {
      if (rowData.ROW_STATUS === ROW_STATE_ADDED) classes.push("grid-row-insert");
      if (rowData.ROW_STATUS === ROW_STATE_EDITED) classes.push("grid-row-update");
      if (rowData.ROW_STATUS === ROW_STATE_DELETED) classes.push("grid-row-delete");
      if (rowData.ROW_STATUS === ROW_STATE_NORMAL) classes.push("grid-row-nomal");
    }

    if (rowData.ROW_STATUS === ROW_STATE_DELETED) {
      classes.push("grid-row-delete");
      if (column.strikeWhenDeleted) classes.push("grid-delete-strike");
    }

    const chkValue = this.props.chkValue;
    if (chkValue) {
      Object.entries(chkValue).forEach(([key, patternObject]) => {
        const className = patternObject?.[rowData?.[key]];
        if (className) classes.push(className);
      });
    }

    if (column.cssClass) classes.push(column.cssClass);
    return classes;
  };

  callFormatItem = (cell, element) => {
    const event = this.makeCellEvent(cell, element);
    this.callProp("onCellUpdate", this.gridAdapter, event);
    this.callProp("onFormatItem", this.gridAdapter, event);
  };

  shouldHideTemplate = (column, rowData) => {
    return Object.entries(column.exceptChkValue).some(([key, value]) => rowData?.[key] === value);
  };

  renderRowStatus = (status, column = {}) => {
    if (!column.showStatusText) return "";
    if (status === ROW_STATE_DELETED) return "삭제 예정";
    if (status === ROW_STATE_ADDED) return "추가";
    if (status === ROW_STATE_EDITED) return "수정";
    return "";
  };

  renderCheckbox = (value) => `<input type="checkbox" ${value ? "checked" : ""} readonly />`;

  renderHeaderCheckbox = () => {
    const rows = this.getRows();
    const checkBoxColumn = this.getCheckBoxColumn();
    const checkedCount = rows.filter((row) => !!row[checkBoxColumn]).length;
    const checked = checkedCount > 0 && checkedCount === rows.length ? "checked" : "";
    return `<input type="checkbox" ${checked} readonly />`;
  };

  createHeaderCheckboxElement = () => {
    const wrapper = document.createElement("span");
    wrapper.className = styles.headerCheckboxWrapper || "";

    const input = document.createElement("input");
    input.type = "checkbox";
    this.headerCheckboxInput = input;
    this.syncHeaderCheckbox();
    input.addEventListener("click", (event) => {
      event.stopPropagation();
      this.toggleHeaderCheckbox(event);
    });

    wrapper.appendChild(input);
    return wrapper;
  };

  renderButton = (value, column) => {
    const classNames = [btnStyles.btn];
    if (column.isButtonIcon) {
      classNames.push(btnStyles.icon);
      if (btnStyles[column.isButtonIcon]) classNames.push(btnStyles[column.isButtonIcon]);
    }
    if (column.isButtonColor && btnStyles[column.isButtonColor]) classNames.push(btnStyles[column.isButtonColor]);
    return `<button type="button" class="${classNames.filter(Boolean).join(" ")}">${this.escapeHtml(value ?? column.header ?? "")}</button>`;
  };

  renderImage = (value, column) => {
    const src = value || column.src || "";
    const alt = column.alt || "";
    return `<img src="${this.escapeHtml(src)}" alt="${this.escapeHtml(alt)}" />`;
  };

  renderLink = (value) => `<button type="button" class="${styles.gridLinkButton}">${this.escapeHtml(value)}</button>`;

  escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[char]));

  escapeCssColor = (value) => String(value ?? "").replace(/[;"'<>]/g, "");

  makeCellContext = (cell, column) => ({
    item: cell.data,
    value: cell.value,
    text: this.getFormattedValue(column, cell.value),
    row: cell.rowIndex,
    col: this.getColumnIndex(column.binding),
    colDef: column,
  });

  getFormattedValue = (column, value) => {
    if (column.dataMap?.length || this.dropDowns[column.binding]?.items?.length) {
      return this.getLookupLabel(column.binding, value);
    }
    return value;
  };

  getLookupLabel = (binding, value) => {
    const dropdown = this.dropDowns[binding];
    if (!dropdown?.items?.length) return value ?? "";
    const item = dropdown.items.find((entry) => String(entry?.[dropdown.codeKey]) === String(value));
    return item?.[dropdown.labelKey] ?? value ?? "";
  };

  handleCellClick = (cell, isDoubleClick) => {
    if (!cell) return;
    const rowIndex = this.getRowIndex(cell.data);
    const field = cell.colDef?.field;
    const colIndex = this.getColumnIndex(field);

    this.selectedIndex = rowIndex;
    this.selectedColIndex = colIndex;
    this.refSelectedIndex.current = rowIndex;
    cell.node?.setSelected?.(true, true);

    const columnDef = this.getFlatColumns()[colIndex];
    const checkBoxColumn = this.getCheckBoxColumn();
    const isCheckboxCell =
      columnDef?.binding === checkBoxColumn || columnDef?.dataType === "Boolean";
    if (isCheckboxCell) {
      const data = cell.data;
      data[columnDef.binding] = !data[columnDef.binding];
      if (this.props.isSingleCheck && data[columnDef.binding]) {
        this.getRows().forEach((item, idx) => {
          if (idx !== rowIndex) item[columnDef.binding] = false;
        });
      }
      this.refreshTable();
    }

    const isInteractiveTarget = !!cell.event?.target?.closest?.("input, button, select, a");
    const shouldToggleRowCheck =
      !isDoubleClick &&
      this.props.isRowCheckOnClick &&
      !isCheckboxCell &&
      !isInteractiveTarget &&
      !!cell.data;

    const handler = isDoubleClick ? this.props.onRowDoubleClick : this.props.onRowClick;
    const eventObject = this.makeCellEvent(cell, cell.event?.target);

    if (isDoubleClick) {
      if (this.clickTimer) clearTimeout(this.clickTimer);
      if (handler) handler(this.gridAdapter, eventObject);
      return;
    }

    if (this.clickTimer) clearTimeout(this.clickTimer);
    this.clickTimer = setTimeout(() => {
      if (shouldToggleRowCheck) {
        cell.data[checkBoxColumn] = !cell.data[checkBoxColumn];
        if (this.props.isSingleCheck && cell.data[checkBoxColumn]) {
          this.getRows().forEach((item, idx) => {
            if (idx !== rowIndex) item[checkBoxColumn] = false;
          });
        }
        this.refreshTable();
      }
      if (handler) handler(this.gridAdapter, eventObject);
    }, 250);
  };

  handleHeaderClick = (event, field, isDoubleClick) => {
    const handler = isDoubleClick ? this.props.onHeaderDoubleClick : this.props.onHeaderClick;
    if (!handler) return;
    handler(this.gridAdapter, this.makeEvent({
      row: -1,
      col: this.getColumnIndex(field),
      target: event?.target,
      panel: this.makePanel(HEADER_PANEL),
      getColumn: () => this.getColumn(field),
    }));
  };

  handleNativeHeaderClick = (event) => {
    const headerElement = event?.target?.closest?.(".ag-header-cell");
    if (!headerElement) return;
    const field = headerElement.getAttribute("col-id");
    if (!field) return;
    this.handleHeaderClick(event, field, false);
  };

  handleNativeHeaderDoubleClick = (event) => {
    const headerElement = event?.target?.closest?.(".ag-header-cell");
    if (!headerElement) return;
    const field = headerElement.getAttribute("col-id");
    if (!field) return;
    this.handleHeaderClick(event, field, true);
  };

  handleCellEditing = (cell) => {
    const field = cell.colDef?.field;
    const column = this.getFlatColumns().find((item) => item.binding === field);
    const event = column ? this.getPendingEditEvent(cell, column) : this.makeCellEvent(cell);
    return !event.cancel;
  };

  handleCellEdited = (cell) => {
    const rowData = cell.data;
    const field = cell.colDef?.field;
    const event = this.makeCellEvent(cell);
    const column = this.getFlatColumns().find((item) => item.binding === field);
    if (column) this.pendingEditEvents.delete(this.getCellEditKey(cell, column));

    this.callProp("onCellEditEnding", this.gridAdapter, event);

    if (rowData && !rowData.ROW_STATUS && cell.oldValue !== rowData[field]) {
      rowData.ROW_STATUS = ROW_STATE_EDITED;
      if (this.props.isUpdateCheck) rowData.ROW_CHECK = true;
    }

    this.callProp("onCellEditEnded", this.gridAdapter, event);
    this.refreshTable();
  };

  handleRowMoved = () => {
    const rows = this.getRows();
    rows.forEach((row) => {
      if (row.ROW_STATUS !== ROW_STATE_ADDED) row.ROW_STATUS = ROW_STATE_EDITED;
      if (this.props.isDragComboBoxCheck) row.ROW_CHECK = true;
    });
    this.setState({ data: rows, rowCount: rows.length }, () => {
      this.callProp("onDraggedRow", this.gridAdapter, this.makeEvent());
    });
  };

  makeCellEvent = (cell, target) => {
    const field = cell.colDef?.field;
    const rowData = cell.data;
    const rowIndex = this.getRowIndex(rowData);
    const colIndex = this.getColumnIndex(field);
    return this.makeEvent({
      row: rowIndex,
      col: colIndex,
      data: cell.oldValue,
      target,
      cell: cell.eGridCell,
      panel: this.makePanel(CELL_PANEL),
      getRow: () => ({ dataItem: rowData, index: rowIndex }),
      getColumn: () => this.getColumn(field),
    });
  };

  makeEvent = (overrides = {}) => ({ cancel: false, stayInEditMode: false, ...overrides });

  hitTest = (event) => {
    const target = event?.target;
    if (!target?.closest) return null;

    const headerElement = target.closest(".ag-header-cell");
    if (headerElement) {
      const field = headerElement.getAttribute("col-id");
      return {
        row: -1,
        col: this.getColumnIndex(field),
        panel: this.makePanel(HEADER_PANEL),
        cellType: HEADER_PANEL,
      };
    }

    const cellElement = target.closest(".ag-cell");
    if (!cellElement) return null;

    const field = cellElement.getAttribute("col-id");
    const rowIndex = Number(cellElement.getAttribute("row-index"));

    return {
      row: rowIndex,
      col: this.getColumnIndex(field),
      panel: this.makePanel(CELL_PANEL),
      cellType: CELL_PANEL,
    };
  };

  makePanel = (type) => {
    if (type === CELL_PANEL && this.cellsPanel) return this.cellsPanel;
    if (type === HEADER_PANEL && this.headerPanel) return this.headerPanel;

    const panel = {
      cellType: type,
      grid: this.gridAdapter,
      get columns() {
        return this.grid.columns;
      },
      get rows() {
        return this.grid.rows;
      },
      getCellData: (row, col) => this.getRows()[row]?.[this.getColumnByIndex(col)?.binding],
      getCellElement: (row, col) => {
        const binding = this.getColumnByIndex(col)?.binding;
        return binding
          ? this.tableHostRef.current?.querySelector(`.ag-cell[row-index="${row}"][col-id="${binding}"]`)
          : this.tableHostRef.current?.querySelector(`.ag-row[row-index="${row}"]`);
      },
    };

    if (type === CELL_PANEL) this.cellsPanel = panel;
    if (type === HEADER_PANEL) this.headerPanel = panel;
    return panel;
  };

  callProp = (name, ...args) => {
    const fn = this.props[name];
    if (typeof fn === "function") return fn(...args);
    return undefined;
  };

  setDataBinding = (datasource, callback) => {
    if (!datasource) return;

    let bindingData = this.normalizeInputData(datasource);
    bindingData = this.applyTreeShape(bindingData);
    bindingData = this.applyCheckColumnType(bindingData);
    bindingData = this.applyNoColumn(bindingData);
    bindingData = this.applyColumnTypes(bindingData);

    this.setState({ data: bindingData, rowCount: bindingData.length }, () => {
      this.selectedIndex = -1;
      this.selectedColIndex = 0;
      this.refSelectedIndex.current = -1;
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", bindingData);
        // Fresh rows arrive unchecked, so the header has to drop its tick with them.
        // Nothing else does it: ag-Grid keeps the header element for the life of the
        // column, which left a checked header sitting above a grid where nothing was
        // selected -- and the next action then reported "select a row first".
        this.syncHeaderCheckbox();
        if (callback) this.afterGridRender(() => callback(this));
      } else if (callback) {
        callback(this);
      }
    });
  };

  afterGridRender = (callback) => {
    if (typeof callback !== "function") return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => callback(this));
    });
  };

  normalizeInputData = (datasource) => {
    const data = normalizeArrayData(datasource);
    return data.map((row, index) => ({
      ...row,
      __e3_grid_id: row?.__e3_grid_id ?? `${Date.now()}_${index}_${Math.random()}`,
    }));
  };

  applyTreeShape = (datasource) => {
    const childKey = this.props.childItemsPath;
    const { stat1: key } = this.props.keyPath;
    if (!childKey || !key) return datasource;

    const duplicated = [];
    datasource.forEach((row) => {
      const children = row[childKey] ? String(row[childKey]).split(",") : [];
      if (!children.length) return;
      const childRows = children.map((child) => datasource.find((item) => item[key] == child)).filter(Boolean);
      row[childKey] = _.orderBy(childRows, ["ORD"], ["asc"]);
      duplicated.push(...childRows);
    });

    const duplicatedKeys = new Set(duplicated.map((row) => row[key]));
    return _.orderBy(datasource.filter((row) => !duplicatedKeys.has(row[key])), ["ORD"], ["asc"]);
  };

  applyCheckColumnType = (bindingData) => {
    const checkBoxColumn = this.getCheckBoxColumn();
    if (!checkBoxColumn || !this.state.columns.find((column) => column.binding === checkBoxColumn)) return bindingData;

    return bindingData.map((row) => {
      const value = row[checkBoxColumn];
      if (typeof value === "string") {
        const lowerValue = value.toLowerCase();
        if (lowerValue === "true" || lowerValue === "1") return { ...row, [checkBoxColumn]: true };
        if (lowerValue === "false" || lowerValue === "0") return { ...row, [checkBoxColumn]: false };
      }
      return row;
    });
  };

  applyNoColumn = (bindingData) => {
    if (!this.props.isNo) return bindingData;
    return bindingData.map((row, index) => {
      const noIndex = this.props.pagingType === "Server" ? Number(this.state.bufferedStartIndex || 1) - 1 + index : index;
      return { NO: noIndex + 1, ...row };
    });
  };

  applyColumnTypes = (bindingData) => {
    const formatColumns = this.getFlatColumns().filter((column) => column.dataType);
    if (!formatColumns.length) return bindingData;

    return bindingData.map((row) => {
      const nextRow = { ...row };
      formatColumns.forEach((column) => {
        const value = nextRow[column.binding];
        if (value == null || value === "") return;
        if (column.dataType === "Number") nextRow[column.binding] = Number(value);
        if (column.dataType === "Boolean") nextRow[column.binding] = value === true || value === 1 || value === "1" || value === "true";
        if (column.dataType === "String") nextRow[column.binding] = value.toString();
      });
      return nextRow;
    });
  };

  clearDataBinding = (callback) => {
    this.setState({ data: [], rowCount: 0, bufferedEndIndex: null }, () => {
      this.selectedIndex = -1;
      if (this.gridApi) this.gridApi.setGridOption("rowData", []);
      if (callback) callback(this);
    });
  };

  clear = () => {
    this.setState({ data: [], columns: [], columnGroups: [], rowCount: 0, bufferedEndIndex: null }, () => {
      this.selectedIndex = -1;
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", []);
        this.gridApi.setGridOption("columnDefs", []);
      }
    });
  };

  getRows = () => this.state.data;

  getRowsByIndex = (idx) => this.getRows()[idx];

  getRowsCount = () => this.getRows().length;

  getCheckBoxColumn = () => {
    if (Object.prototype.hasOwnProperty.call(this, "checkBoxColumn")) return this.checkBoxColumn;
    return this.props.checkBoxColumn;
  };

  getCheckedRows = () => {
    const checkBoxColumn = this.getCheckBoxColumn();
    if (!checkBoxColumn) return [];
    return this.getRows().filter((row) => !!row[checkBoxColumn]);
  };

  setCheckedRow = (idx, isChk = true) => {
    const row = this.getRows()[idx];
    if (!row) return;
    const checkBoxColumn = this.getCheckBoxColumn();
    if (!checkBoxColumn) return;
    row[checkBoxColumn] = isChk;
    this.refreshTable();
  };

  getSelectedRow = () => (this.selectedIndex > -1 ? this.getRows()[this.selectedIndex] : undefined);

  getSelectedRowIndex = () => this.selectedIndex;

  getAddedRows = () => this.getRows().filter((row) => row.ROW_STATUS === ROW_STATE_ADDED);

  getEditedRows = () => this.getRows().filter((row) => row.ROW_STATUS === ROW_STATE_EDITED);

  getDeletedRows = () => this.getRows().filter((row) => row.ROW_STATUS === ROW_STATE_DELETED);

  getModifiedRows = () => [
    ...this.getAddedRows(),
    ...this.getEditedRows(),
    ...this.getDeletedRows(),
  ];

  addRow = (item = {}) => {
    const row = { __e3_grid_id: `${Date.now()}_${Math.random()}`, ROW_STATUS: ROW_STATE_ADDED, ROW_CHECK: true, ...item };
    const nextData = [...this.getRows(), row];
    this.state.data = nextData;
    this.state.rowCount = nextData.length;
    this.setState({ data: nextData, rowCount: nextData.length }, () => {
      this.refreshTable();
      this.selectionByIndex(nextData.length - 1);
    });

    const event = this.makeEvent({ row: nextData.length - 1, col: 0, dataItem: row });
    this.callProp("onRowAdded", this.gridAdapter, event);
    return event;
  };

  deleteRow = (idx) => {
    const index = idx === 0 || idx ? Number(idx) : this.getRowsCount() - 1;
    const rows = this.getRows();
    const row = rows[index];
    if (!row) return undefined;

    if (this.props.isRowStatus === false || row.ROW_STATUS === ROW_STATE_ADDED || !row.ROW_STATUS) {
      rows.splice(index, 1);
    } else if (row.ROW_STATUS === ROW_STATE_DELETED) {
      row.ROW_STATUS = "";
      row.ROW_CHECK = false;
    } else {
      row.ROW_STATUS = ROW_STATE_DELETED;
      row.ROW_CHECK = true;
    }

    this.setState({ data: rows, rowCount: rows.length }, () => this.refreshTable());
    const event = this.makeEvent({ row: index, col: this.selectedColIndex });
    this.callProp("onDeletingRow", this.gridAdapter, event);
    return event;
  };

  deleteCheckedRow = () => {
    for (let i = this.getRows().length - 1; i >= 0; i--) {
      if (this.getRows()[i]?.ROW_CHECK) this.deleteRow(i);
    }
  };

  setCheckAll = (isCheck) => {
    const checkBoxColumn = this.getCheckBoxColumn();
    if (!checkBoxColumn) return;
    this.getRows().forEach((row) => {
      row[checkBoxColumn] = isCheck;
    });
    this.refreshTable();
  };

  clearSelection = () => {
    this.selectedIndex = -1;
    this.refSelectedIndex.current = -1;
    this.pendingSelection = null;
    if (this.gridApi) this.gridApi.deselectAll();
  };

  selectionByIndex = (idx = 0) => {
    const index = Number(idx);
    const rowCount = this.gridApi?.getDisplayedRowCount?.() ?? this.getRowsCount();
    if (!Number.isInteger(index) || index < 0 || index >= rowCount) {
      this.clearSelection();
      return;
    }

    this.selectedIndex = index;
    this.refSelectedIndex.current = index;
    if (this.gridApi) {
      const row = this.gridApi.getDisplayedRowAtIndex(index);
      if (!row) {
        this.pendingSelection = index;
        return;
      }
      row.setSelected(true, true);
      this.gridApi.ensureIndexVisible(index);
    }
  };

  flushPendingSelection = () => {
    if (this.pendingSelection == null) return;
    const index = this.pendingSelection;
    this.pendingSelection = null;
    this.selectionByIndex(index);
  };

  clearAddRows = () => {
    const nextData = this.getRows().filter((row) => row.ROW_STATUS !== ROW_STATE_ADDED);
    this.setState({ data: nextData, rowCount: nextData.length }, () => this.refreshTable());
  };

  setCellData = (rowIndex, column, value, callback) => {
    const col = typeof column === "number" ? this.getColumnByIndex(column) : this.getColumn(column);
    const row = this.getRows()[rowIndex];
    if (!row || !col) return;
    row[col.binding] = value;
    this.refreshTable();
    if (callback) callback(this);
  };

  setDropDown = (_column = "", _dataMap = [], _dataMapCode = "codeId", _dataMapValue = "codeName", _firstSelected = false, callback) => {
    if (!_column) return;
    this.dropDowns[_column] = { items: _dataMap || [], codeKey: _dataMapCode, labelKey: _dataMapValue };

    const firstValue = _firstSelected && _dataMap?.length ? _dataMap[0][_dataMapCode] : "";

    // Functional update on purpose: this is often called from an earlier binding's
    // afterGridRender callback while a newer setDataBinding is still pending. Reading
    // this.state.data here and writing it back replaced the new rows with the stale
    // (often empty) array, leaving the grid blank although data had arrived.
    // Returning null leaves data untouched when there is no default to fill.
    this.setState((prev) => {
      if (firstValue === "") return null;
      prev.data.forEach((row) => {
        if (row[_column] == null) row[_column] = firstValue;
      });
      return { data: prev.data };
    }, () => {
      this.applyColumns();
      this.refreshTable();
      if (callback) callback(this);
    });
  };

  setColumns = (columns) => {
    this.setState({ columns: columns || [] });
  };

  setColumngroups = (colgroup) => {
    this.setState({ columnGroups: colgroup || [] });
  };

  pushHeader = (arrays = "", option = "", callback) => {
    const columns = option || [{ binding: "TEMP", header: "TEMP", align: "center", width: 150, isReadOnly: this.state.isReadOnly }];
    this.setState({ columns, columnGroups: [] }, () => this.setDataBinding(arrays || [], callback));
  };

  remakeHeader = (arrays = "", option = "", callback) => {
    this.pushHeader(arrays, option, callback);
  };

  removeHeaderByIndex = (index = 0, callback) => {
    const columns = [...this.state.columns];
    columns.splice(index, 1);
    this.setState({ columns }, () => {
      if (callback) callback(this);
    });
  };

  removeHeaderByColumn = (columnName = "", callback) => {
    const columns = this.state.columns.filter((column) => column.binding !== columnName);
    this.setState({ columns }, () => {
      if (callback) callback(this);
    });
  };

  setIsReadOnly = (value) => {
    this.setState({ isReadOnly: value });
  };

  getIsReadOnly = () => this.state.isReadOnly;

  ["get" + "Collection" + "View"] = () => this.collectionLike;

  getColumnIndex = (column = null) => {
    if (!column) return -1;
    return this.getFlatColumns().findIndex((item) => item.binding === column.toString());
  };

  getColumnByIndex = (idx) => this.getFlatColumns()[idx];

  getColumn = (column = "") => {
    const columns = this.getFlatColumns();
    const index = columns.findIndex((item) => item.binding === column);
    if (index < 0) return undefined;
    return this.makeColumnAdapter(columns[index], index);
  };

  getCurrentPosition = () => this.selectedIndex;

  setCurrentPosition = (idx) => {
    this.selectionByIndex(idx);
  };

  moveUp = () => {
    const oldIndex = this.getCurrentPosition();
    if (oldIndex <= 0) return;
    const rows = this.getRows();
    rows.splice(oldIndex - 1, 0, rows.splice(oldIndex, 1)[0]);
    this.setState({ data: rows }, () => {
      this.refreshTable();
      this.selectionByIndex(oldIndex - 1);
    });
  };

  moveDown = () => {
    const oldIndex = this.getCurrentPosition();
    if (oldIndex < 0 || oldIndex >= this.getRows().length - 1) return;
    const rows = this.getRows();
    rows.splice(oldIndex + 1, 0, rows.splice(oldIndex, 1)[0]);
    this.setState({ data: rows }, () => {
      this.refreshTable();
      this.selectionByIndex(oldIndex + 1);
    });
  };

  forceUpdates = () => {
    this.refreshTable();
    this.forceUpdate();
  };

  viewUpdates = (callback) => {
    this.refreshTable();
    if (callback) callback(this);
  };

  freezeRows = () => {};

  freezeColumns = () => {};

  chkArray = (arr) => !!arr?.length;

  setToggleClass = (cell, css, condition) => {
    if (!cell || !css) return;
    const classNames = Array.isArray(css) ? css : String(css).split(" ");
    classNames.filter(Boolean).forEach((className) => {
      cell.classList.toggle(className, !!condition);
    });
  };

  setRemoveClass = (cell, css) => {
    if (!cell || !css) return;
    const classNames = Array.isArray(css) ? css : String(css).split(" ");
    classNames.filter(Boolean).forEach((className) => cell.classList.remove(className));
  };

  chkValidate = () => {
    if (!this.state.validateInfo) return true;
    let valid = true;
    const columns = this.getFlatColumns();
    const rows = this.getRows();

    rows.forEach((row) => {
      columns.forEach((column) => {
        const validInfo = this.state.validateInfo?.[column.binding];
        if (!validInfo) return;
        try {
          validate(getValidateObject(validInfo, column.header, row[column.binding]));
        } catch (ex) {
          row.ERR_MSG = ex;
          valid = false;
        }
      });
    });

    this.refreshTable();
    return valid;
  };

  setBufferedInfo = (idx, cnt, callback) => {
    this.setState(
      {
        bufferedStartIndex: idx ? Number(idx) : 1,
        bufferedDataCount: cnt ? Number(cnt) : Number(this.state.bufferedDataCount),
      },
      callback
    );
  };

  getBufferedInfo = () => ({
    idx: Number(this.state.bufferedStartIndex),
    cnt: Number(this.state.bufferedDataCount),
    end: Number(this.state.bufferedEndIndex),
    cur: Math.ceil(this.state.bufferedStartIndex / this.state.bufferedDataCount),
  });

  setBufferedEndIndex = (idx) => {
    this.setState({ bufferedEndIndex: Number(idx) });
  };

  setPageButtons = (endIdx, param = null) => {
    this.refProgramParam.current = param;
    const index = Number(this.state.bufferedStartIndex);
    const count = Number(this.state.bufferedDataCount);
    const totalPageCount = Math.ceil(endIdx / count);
    const currentPage = Math.ceil((index - 1) / count) + 1;
    // pageButtonCount is the most page numbers to render; a long result set reads as
    // 1 … 6 7 8 … 20 rather than one block of ten.
    const pageButton = pageWindow(currentPage, totalPageCount, this.props.pageButtonCount);
    const activeButton = pageButton.map((page) => (page === currentPage ? 1 : 0));

    // Index past the end (rows deleted, filter narrowed) — pull it back to the last
    // page that exists. Compared against totalPageCount, not the last rendered
    // button: once currentPage is a whole block beyond the end, pageButton comes out
    // empty and the old `pageButton[last] < currentPage` test could never fire, which
    // left the screen on an empty grid with no page numbers to click.
    const lastPage = Math.max(1, totalPageCount);
    if (endIdx !== 0 && currentPage > lastPage && this.refSearchCnt.current < 3) {
      this.refSearchCnt.current += 1;
      const nextIdx = (lastPage - 1) * count + 1;
      this.setBufferedInfo(nextIdx, count);
      // the frozen param has to ride along, or this refetch reads whatever the search
      // form says now instead of the result set the pager is paging through
      this.props.bufferedSearchFunction?.(nextIdx, this.refProgramParam.current);
      return;
    }

    this.refSearchCnt.current = 0;
    this.setState({ pageButton, activeButton, bufferedEndIndex: endIdx });
  };

  setPageIndex = (idx = 1, param) => {
    const startIndex = idx > 0 ? idx : this.state.bufferedStartIndex;
    const dataCount = this.state.bufferedDataCount;
    this.setBufferedInfo(startIndex, dataCount);

    if (Array.isArray(param)) {
      return param.map((item) => ({ ...item, START_INDEX: startIndex, DATA_COUNT: dataCount }));
    }
    if (param && typeof param === "object") {
      param.START_INDEX = startIndex;
      param.DATA_COUNT = dataCount;
      return param;
    }
    return { OBJECT: param, START_INDEX: startIndex, DATA_COUNT: dataCount };
  };

  saveExcel = (colHeader = true, style = true, fileName = "grid.xlsx", isCheckedItem = false) => {
    const exportFileName = fileName?.toLowerCase?.().endsWith(".xlsx")
      ? fileName
      : `${fileName || "grid"}.xlsx`;

    return saveGridLikeWorkbook([this.gridAdapter], {
      includeColumnHeaders: colHeader,
      includeStyles: style,
      fileName: exportFileName,
      isCheckedItem,
      includeColumns: (column) => column.visible !== false,
    });
  };

  loadExcel = (headeRangeIdx = 0, callBack = "") => {
    this.excelHeaderRowIdx = headeRangeIdx;
    this.excelCallback = callBack;
    this.useRefInput.current?.click();
  };

  loadExcelMakeGridData = async (file, notMerge) => {
    if (!file) return;
    const columns = this.getFlatColumns().filter((column) => column.binding !== "ROW_CHECK" && column.binding !== "ROW_STATUS");
    const mappedRows = await readExcelRows(file, {
      headerRowIndex: Number(this.excelHeaderRowIdx),
      columns,
    });

    this.setDataBinding(mappedRows, () => {
      if (this.excelCallback) this.excelCallback(this);
      if (this.useRefInput.current) this.useRefInput.current.value = "";
    });
  };

  applyColumns = () => {
    if (this.gridApi) {
      this.gridApi.setGridOption("columnDefs", this.getAgGridColumns());
      this.redrawTable();
    }
  };

  refreshTable = () => {
    if (!this.gridApi) return;
    this.gridApi.setGridOption("rowData", [...this.getRows()]);
    this.redrawTable();
    this.syncHeaderCheckbox();
  };

  getRowIndex = (rowData) => this.getRows().findIndex((row) => row === rowData || row.__e3_grid_id === rowData?.__e3_grid_id);

  /**
   * Pushes the rows' check state onto the header checkbox.
   *
   * The header input is a plain DOM node ag-Grid owns for the life of the header, so
   * nothing updates it on its own: ticking rows one by one left it showing unchecked
   * however many rows were ticked. Every path that changes a check runs through
   * refreshTable, so that is where this is called from.
   */
  syncHeaderCheckbox = () => {
    const input = this.headerCheckboxInput;
    if (!input) return;
    const checkBoxColumn = this.getCheckBoxColumn();
    if (!checkBoxColumn) return;
    const rows = this.getRows();
    const checkedCount = rows.filter((row) => !!row[checkBoxColumn]).length;
    input.checked = rows.length > 0 && checkedCount === rows.length;
    input.indeterminate = checkedCount > 0 && checkedCount < rows.length;
  };

  toggleHeaderCheckbox = () => {
    const checkBoxColumn = this.getCheckBoxColumn();
    if (!checkBoxColumn) return;
    const rows = this.getRows();
    // Read the intent from the rows, not from the checkbox the browser just toggled.
    // A stale header (see syncHeaderCheckbox) made that toggle mean the opposite of
    // what the user saw, so "clear all" first re-checked everything.
    const checked = !(rows.length > 0 && rows.every((row) => !!row[checkBoxColumn]));
    rows.forEach((row) => {
      row[checkBoxColumn] = checked;
    });
    this.refreshTable();
  };

  makeColumnAdapter = (column, index) => {
    const self = this;
    const dropdown = this.dropDowns[column.binding] || {
      items: column.dataMap || [],
      codeKey: column.dataMapCode || "codeId",
      labelKey: column.dataMapValue || "codeName",
    };
    const editor = {
      dropDown: {
        get itemsSource() {
          return self.dropDowns[column.binding]?.items || dropdown.items;
        },
        set itemsSource(items) {
          const current = self.dropDowns[column.binding] || dropdown;
          self.dropDowns[column.binding] = {
            items: Array.isArray(items) ? items : [],
            codeKey: current.codeKey || column.dataMapCode || "codeId",
            labelKey: current.labelKey || column.dataMapValue || "codeName",
          };
        },
      },
      isDroppedDown: false,
    };
    return {
      ...column,
      binding: column.binding,
      header: column.header,
      index,
      visible: column.visible !== false,
      dataMap: dropdown.items?.length ? dropdown : null,
      editor,
    };
  };

  createGridAdapter = () => {
    const self = this;
    const adapter = {
      get rows() {
        return self.getRows().map((dataItem, index) => ({ dataItem, index, visible: true }));
      },
      get columns() {
        return self.getFlatColumns().map((column, index) => self.makeColumnAdapter(column, index));
      },
      get _cols() {
        return this.columns;
      },
      get cells() {
        return self.makePanel(CELL_PANEL);
      },
      get collectionView() {
        return self.collectionLike;
      },
      get selectedRows() {
        return self.selectedIndex > -1 ? [{ index: self.selectedIndex, dataItem: self.getSelectedRow() }] : [];
      },
      get selectedItems() {
        return self.getSelectedRow() ? [self.getSelectedRow()] : [];
      },
      get selection() {
        return { row: self.selectedIndex, col: self.selectedColIndex };
      },
      get itemsSource() {
        return self.getRows();
      },
      set itemsSource(value) {
        self.setDataBinding(value);
      },
      getColumn: (column) => self.getColumn(column),
      setCellData: (...args) => self.setCellData(...args),
      get hostElement() {
        return self.tableHostRef.current;
      },
      hitTest: (event) => self.hitTest(event),
      startEditing: (_fullEdit = true, row = self.selectedIndex, col = self.selectedColIndex) => {
        const column = typeof col === "number" ? self.getColumnByIndex(col) : self.getColumn(col);
        const rowIndex = Number(row);
        const colKey = column?.binding || col;
        if (!self.gridApi || !Number.isInteger(rowIndex) || rowIndex < 0 || !colKey) return false;
        self.gridApi.startEditingCell?.({ rowIndex, colKey });
        return true;
      },
      select: (row, col = 0) => {
        self.selectedColIndex = col;
        self.selectionByIndex(row);
      },
      refresh: () => self.refreshTable(),
      scrollIntoView: () => {},
      collapseGroupsToLevel: () => {},
      focus: () => {},
      beginUpdate: () => {},
      endUpdate: () => {},
      addEventListener: () => {},
      onRowAdded: (event) => self.callProp("onRowAdded", adapter, event),
      onDeletingRow: (event) => self.callProp("onDeletingRow", adapter, event),
    };
    return adapter;
  };

  createCollectionLike = () => {
    const self = this;
    const applySortDescriptions = (values) => {
      if (!self.gridApi?.applyColumnState) return;
      self.gridApi.applyColumnState({
        defaultState: { sort: null },
        state: values
          .filter((item) => item?.property)
          .map((item) => ({
            colId: item.property,
            sort: item.ascending === false ? "desc" : "asc",
          })),
      });
    };
    const sortDescriptions = {
      values: [],
      clear() {
        this.values = [];
        applySortDescriptions(this.values);
      },
      push(sortItem) {
        this.values.push(sortItem);
        applySortDescriptions(this.values);
      },
    };

    return {
      get items() {
        return self.getRows();
      },
      get sourceCollection() {
        return self.getRows();
      },
      get currentItem() {
        return self.getSelectedRow();
      },
      get currentPosition() {
        return self.selectedIndex;
      },
      set currentPosition(value) {
        self.selectionByIndex(value);
      },
      sortDescriptions,
      groupDescriptions: [],
      moveCurrentToPosition: (idx) => self.selectionByIndex(idx),
      moveCurrentToLast: () => self.selectionByIndex(self.getRowsCount() - 1),
      addNew: () => {
        const event = self.addRow({});
        return event.dataItem;
      },
      removeAt: (idx) => {
        const rows = self.getRows();
        rows.splice(idx, 1);
        self.setState({ data: rows, rowCount: rows.length }, () => self.refreshTable());
      },
      remove: (item) => {
        const rows = self.getRows().filter((row) => row !== item);
        self.setState({ data: rows, rowCount: rows.length }, () => self.refreshTable());
      },
      refresh: () => self.refreshTable(),
    };
  };

  CreateGridTop(leftWrap, rightWrap) {
    if (rightWrap?.btnExcel && !rightWrap.btnExcel.onClick) {
      rightWrap.btnExcel.onClick = (menuName) => {
        const now = new Date();
        const strDate = getDate2String(now, "yyyyMMddHHddss");
        this.saveExcel(true, true, `${menuName ?? "sheet"}_${strDate}.xlsx`);
      };
    }

    const leftComponents = leftWrap ? Object.keys(leftWrap).filter((key) => key !== "cnt" && key !== "title").length : 0;
    const hasTop = leftWrap?.cnt || leftWrap?.title || leftComponents > 0 || (rightWrap && Object.keys(rightWrap).length);

    return hasTop ? (
      <div className={styles.gridLeftRightWrap}>
        {leftWrap && (
          <>
            <div className={styles.gridControlLeftWrap}>
              {leftWrap.title && <span className="divTitle">{leftWrap.title}</span>}
              {leftWrap.cnt && (
                <>
                  <span className="divTitle_count">{this.state.bufferedEndIndex ?? this.state.rowCount}</span>
                  <p>건</p>
                </>
              )}
            </div>
            {leftComponents > 0 && <div>{createBtnArea({ ...leftWrap })}</div>}
          </>
        )}
        {rightWrap && <div className={styles.gridControlRightWrap}>{createBtnArea({ ...rightWrap })}</div>}
      </div>
    ) : (
      <div className={styles.gridControlRightWrap}></div>
    );
  }

  renderPaging = () => {
    if (this.props.pagingType !== "Server") return null;
    // An empty page keeps its pager. total > 0 with 0 rows means the start index
    // ran past the end (rows deleted, filter narrowed) and |< is the only way
    // back — hiding the pager there strands the screen.
    if (!this.state.data.length && !this.state.bufferedEndIndex) return null;

    const info = this.getBufferedInfo();
    // The screen's own page size has to be in the option list or the select displays
    // the first option instead: bufferedDataCount={10} reads as "50". Both the preset
    // (prop) and the live value (state) go in, so picking 50 doesn't lose the 10.
    const rowOptions = [
      ...new Set(
        [Number(this.props.bufferedDataCount), info.cnt, 10, 20, 50, 100].filter((n) => n > 0)
      ),
    ].sort((a, b) => a - b);
    const search = (idx) => {
      this.setBufferedInfo(idx);
      this.props.bufferedSearchFunction?.(idx, this.refProgramParam.current);
    };

    return (
      <div className="pagingDiv_wrap">
        <div className="pagingDiv_left">
          <span className={styles.gridPagingComboTitle}>Show Rows</span>
          <select
            className={styles.gridPagingCombo}
            value={this.state.bufferedDataCount}
            onChange={(event) => {
              // The search reads the new size back out of state, so it has to wait for
              // the commit. A setState callback is that guarantee; the 100ms timeout
              // this replaces was a guess that a slow frame could outlast.
              this.setBufferedInfo(1, Number(event.target.value), () =>
                this.props.bufferedSearchFunction?.(1, this.refProgramParam.current)
              );
            }}
          >
            {rowOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>
        <div className="pagingDiv">
          <button type="button" onClick={() => info.idx !== 1 && search(1)}>|&lt;</button>
          <button type="button" onClick={() => info.idx - info.cnt >= 1 && search(info.idx - info.cnt)}>&lt;</button>
          {this.state.activeButton.map((active, index) => {
            const page = this.state.pageButton[index];
            if (page === PAGE_GAP) {
              return (
                <span key={`paging_gap_${index}`} className={styles.gridPageGap}>
                  {PAGE_GAP}
                </span>
              );
            }
            return (
              <button
                key={`paging_${page}`}
                type="button"
                className={active ? styles.gridPageActive : ""}
                onClick={() => {
                  const pageNum = Number(page);
                  if (pageNum !== info.cur) search((pageNum - 1) * info.cnt + 1);
                }}
              >
                {page}
              </button>
            );
          })}
          <button type="button" onClick={() => info.idx + info.cnt <= info.end && search(info.idx + info.cnt)}>&gt;</button>
          <button
            type="button"
            onClick={() => {
              const endPage = Math.ceil(info.end / info.cnt);
              const idx = Math.max(1, (endPage - 1) * info.cnt + 1);
              if (idx !== info.idx) search(idx);
            }}
          >
            &gt;|
          </button>
        </div>
      </div>
    );
  };

  render() {
    const { rightWrap, leftWrap } = this.props;

    return (
      <div className={styles.container}>
        {this.CreateGridTop(leftWrap, rightWrap)}
        <div
          ref={this.tableHostRef}
          className={`${styles.agGrid} ag-theme-balham ${this.props.cssClass || ""}`}
          style={{ width: this.props.width || "100%", ...(this.resolveHeight() ? { height: this.resolveHeight() } : {}), ...this.props.style }}
        />
        {this.state.data.length === 0 && <div className={styles.gridEmpty}></div>}
        {this.props.exportExcel ? (
          <button type="button" onClick={() => this.saveExcel()} className="btn btn-default">
            Export
          </button>
        ) : null}
        {this.renderPaging()}
        <input
          ref={this.useRefInput}
          className="input-excel-import"
          style={{ display: "none" }}
          type="file"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(event) => this.loadExcelMakeGridData(event.target.files?.[0], this.props.notMerge)}
        />
      </div>
    );
  }
}

DataGrid.propTypes = {
  style: PropTypes.object,
  theme: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  columns: PropTypes.array,
  columnGroups: PropTypes.array,
  allowAddNew: PropTypes.bool,
  allowDelete: PropTypes.bool,
  selectionMode: PropTypes.string,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onHeaderClick: PropTypes.func,
  onHeaderDoubleClick: PropTypes.func,
  sortDescriptions: PropTypes.array,
  groupDescriptions: PropTypes.array,
  trackChanges: PropTypes.bool,
  filter: PropTypes.bool,
  quickAutoSize: PropTypes.bool,
  allowPinning: PropTypes.string,
  isNo: PropTypes.bool,
  codeId: PropTypes.string,
  frozenRows: PropTypes.number,
  frozenColumns: PropTypes.number,
  allowMerging: PropTypes.string,
  isReadOnly: PropTypes.bool,
  exportExcel: PropTypes.bool,
  defaultColumn: PropTypes.bool,
  onCellUpdate: PropTypes.func,
  onCellEditEnding: PropTypes.func,
  onCellEditEnded: PropTypes.func,
  onRowAdded: PropTypes.func,
  onDeletingRow: PropTypes.func,
  prepareCellForEdit: PropTypes.func,
  beginningEdit: PropTypes.func,
  sortedColumn: PropTypes.func,
  isAllCheckbox: PropTypes.bool,
  checkBoxColumn: PropTypes.string,
  validateYmlFile: PropTypes.object,
  gridId: PropTypes.string,
  pagingType: PropTypes.string,
  bufferedStartIndex: PropTypes.number,
  bufferedDataCount: PropTypes.number,
  bufferedSearchFunction: PropTypes.func,
  pageButtonCount: PropTypes.number,
  isRestrictedMergeManager: PropTypes.bool,
  childItemsPath: PropTypes.string,
  isCollapseGroupsToLevel0: PropTypes.bool,
  onloadedRows: PropTypes.func,
  onFormatItem: PropTypes.func,
  onDraggedRow: PropTypes.func,
  isColumnFooters: PropTypes.bool,
  isInputFilter: PropTypes.bool,
  isSearchFilter: PropTypes.object,
  isDragColumn: PropTypes.bool,
  isDragComboBoxCheck: PropTypes.bool,
  isAutoRowHeights: PropTypes.bool,
  headerStyle: PropTypes.array,
  columnFooterName: PropTypes.object,
  exportFileName: PropTypes.string,
  isContextMenu: PropTypes.bool,
  onCopying: PropTypes.func,
  onPasted: PropTypes.func,
  onPastedCell: PropTypes.func,
  onPasting: PropTypes.func,
  onPastingCell: PropTypes.func,
  cssClass: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  autoGenerateColumns: PropTypes.bool,
  headersVisibility: PropTypes.string,
  leftWrap: PropTypes.object,
  rightWrap: PropTypes.object,
  isHdrTooltip: PropTypes.bool,
  isTooltip: PropTypes.bool,
  oneClickEdit: PropTypes.bool,
  isRequired: PropTypes.bool,
  isMultiLine: PropTypes.bool,
  isWordWrap: PropTypes.bool,
  isAllowSorting: PropTypes.bool,
  isAllowDragging: PropTypes.bool,
  /** 행 높이(px). 2행 셀처럼 내용이 높은 화면에서 올려 쓴다 */
  rowHeight: PropTypes.number,
  isUpdateCheck: PropTypes.bool,
  notMerge: PropTypes.bool,
  keyPath: PropTypes.object,
  isAllowListBox: PropTypes.bool,
  isSingleCheck: PropTypes.bool,
  isRowCheckOnClick: PropTypes.bool,
  chkValue: PropTypes.object,
  source: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  isRowStatus: PropTypes.bool,
};

DataGrid.defaultProps = {
  width: "100%",
  height: "100%",
  columns: [],
  columnGroups: [],
  allowAddNew: false,
  allowDelete: true,
  selectionMode: "Row",
  onRowClick: null,
  onRowDoubleClick: null,
  onHeaderClick: null,
  onHeaderDoubleClick: null,
  sortDescriptions: [],
  groupDescriptions: [],
  trackChanges: true,
  filter: false,
  quickAutoSize: true,
  allowPinning: "",
  isNo: true,
  frozenRows: 0,
  frozenColumns: 0,
  allowMerging: "Cells",
  isReadOnly: true,
  exportExcel: false,
  defaultColumn: false,
  onCellUpdate: null,
  onCellEditEnding: null,
  onCellEditEnded: null,
  onRowAdded: null,
  onDeletingRow: null,
  prepareCellForEdit: null,
  beginningEdit: null,
  sortedColumn: null,
  isAllCheckbox: false,
  checkBoxColumn: "CHK",
  validateYmlFile: null,
  gridId: null,
  pagingType: "None",
  bufferedStartIndex: 1,
  bufferedDataCount: 50,
  bufferedSearchFunction: null,
  pageButtonCount: 5,
  isRestrictedMergeManager: false,
  childItemsPath: "",
  isCollapseGroupsToLevel0: false,
  onloadedRows: null,
  onFormatItem: null,
  onDraggedRow: null,
  isColumnFooters: false,
  isInputFilter: false,
  isSearchFilter: null,
  isDragColumn: false,
  isDragComboBoxCheck: true,
  isAutoRowHeights: false,
  headerStyle: [],
  columnFooterName: {},
  exportFileName: "",
  isContextMenu: true,
  onCopying: null,
  onPasted: null,
  onPastedCell: null,
  onPasting: null,
  onPastingCell: null,
  cssClass: "",
  autoGenerateColumns: false,
  headersVisibility: "Column",
  leftWrap: { cnt: false },
  rightWrap: null,
  isHdrTooltip: false,
  isTooltip: false,
  oneClickEdit: true,
  isRequired: false,
  isMultiLine: false,
  isWordWrap: false,
  isAllowSorting: true,
  isAllowDragging: true,
  rowHeight: 36,
  isUpdateCheck: false,
  notMerge: false,
  keyPath: { stat1: "MENU_ID", stat2: "PARENT_ID" },
  isAllowListBox: true,
  isSingleCheck: false,
  isRowCheckOnClick: false,
  chkValue: null,
  source: null,
  data: null,
  isRowStatus: true,
};

export default DataGrid;
